/**
 * Mining Simulation Engine
 *
 * Core standalone module that handles all mining simulation logic:
 * - 60-second round timer
 * - Random block selection
 * - Win/loss calculations
 * - MINER token distribution
 * - Motherlode system
 *
 * This module is COMPLETELY INDEPENDENT and can be:
 * - Modified without affecting other features
 * - Tested in isolation
 * - Replaced with real blockchain logic later
 */

import { selectRandomBlock, checkMotherlode } from './randomness';
import {
    calculateSOLRewards,
    calculateMINERRewards,
    updateMotherlodePot,
} from './calculations';
import { MINING_CONFIG } from '../config/constants';

/**
 * Create initial mining state
 */
export function createInitialMiningState() {
    return {
        currentRound: 1,
        timeLeft: MINING_CONFIG.ROUND_DURATION_SECONDS,
        isActive: false,
        lastWinningBlock: null,
        motherlodePot: MINING_CONFIG.MOTHERLODE_INCREMENT,
        roundHistory: [],
        userDeployments: {}, // blockNum -> SOL amount
        totalDeployedSOL: 0,
        // User balances
        unrefinedMINER: 0,
        refinedMINER: 0,
        pendingSOLRewards: 0,
        // Statistics
        totalMined: 0,
        totalInvested: 0,
        totalRounds: 0,
        totalWins: 0,
    };
}

/**
 * Process the end of a round
 *
 * @param {Object} state - Current mining state
 * @param {Object} allDeployments - All deployments from all users (simulated)
 * @returns {Object} Updated state and round result
 */
export function processRoundEnd(state, allDeployments = null) {
    // Use user's deployments if no all deployments provided (single player simulation)
    const blockDeployments = allDeployments || state.userDeployments;

    // Calculate total SOL in pool
    const totalPoolSOL = Object.values(blockDeployments).reduce((sum, val) => sum + val, 0);

    if (totalPoolSOL === 0) {
        // No deployments, just increment round
        return {
            newState: {
                ...state,
                currentRound: state.currentRound + 1,
                timeLeft: MINING_CONFIG.ROUND_DURATION_SECONDS,
            },
            roundResult: null,
        };
    }

    // Select winning block
    const winningBlock = selectRandomBlock();

    // Check for motherlode
    const motherlodeHit = checkMotherlode();

    // Calculate user's results
    const userDeployedBlocks = Object.keys(state.userDeployments).map(Number);
    const userWon = userDeployedBlocks.includes(winningBlock);

    let solReward = 0;
    let minerReward = 0;
    let motherlodeReward = 0;

    if (userWon) {
        // User won!
        const userWinningSOL = state.userDeployments[winningBlock] || 0;
        const totalWinningSOL = blockDeployments[winningBlock] || userWinningSOL;

        // Calculate SOL rewards
        const solCalc = calculateSOLRewards(winningBlock, blockDeployments, totalPoolSOL);
        const proportion = totalWinningSOL > 0 ? userWinningSOL / totalWinningSOL : 0;
        solReward = solCalc.winnerReward * proportion;

        // Calculate MINER rewards
        const minerCalc = calculateMINERRewards(
            userWinningSOL,
            totalWinningSOL,
            motherlodeHit,
            state.motherlodePot
        );
        minerReward = minerCalc.minerTokens;
        motherlodeReward = minerCalc.motherlodeReward;
    }

    // Update motherlode pot
    const newMotherlodePot = updateMotherlodePot(state.motherlodePot, motherlodeHit);

    // Create round result
    const roundResult = {
        roundNumber: state.currentRound,
        winningBlock,
        userWon,
        userDeployedBlocks,
        solReward,
        minerReward,
        motherlodeHit,
        motherlodeReward,
        totalPoolSOL,
        timestamp: Date.now(),
    };

    // Calculate new balances
    const newUnrefinedMINER = state.unrefinedMINER + minerReward + motherlodeReward;
    const newPendingSOL = state.pendingSOLRewards + (userWon ? solReward : 0) - state.totalDeployedSOL;

    // Update statistics
    const newTotalMined = state.totalMined + minerReward + motherlodeReward;
    const newTotalInvested = state.totalInvested + state.totalDeployedSOL;
    const newTotalWins = state.totalWins + (userWon ? 1 : 0);
    const newTotalRounds = state.totalRounds + 1;

    // Create new state
    const newState = {
        ...state,
        currentRound: state.currentRound + 1,
        timeLeft: MINING_CONFIG.ROUND_DURATION_SECONDS,
        lastWinningBlock: winningBlock,
        motherlodePot: newMotherlodePot,
        roundHistory: [roundResult, ...state.roundHistory].slice(0, 20), // Keep last 20 rounds
        unrefinedMINER: newUnrefinedMINER,
        pendingSOLRewards: newPendingSOL,
        totalMined: newTotalMined,
        totalInvested: newTotalInvested,
        totalWins: newTotalWins,
        totalRounds: newTotalRounds,
        // Reset deployments for next round
        userDeployments: {},
        totalDeployedSOL: 0,
    };

    return {
        newState,
        roundResult,
    };
}

/**
 * Deploy SOL to blocks
 *
 * @param {Object} state - Current mining state
 * @param {Array} blockNumbers - Array of block numbers to deploy to
 * @param {number} solPerBlock - SOL amount per block
 * @returns {Object} Updated state
 */
export function deployToBlocks(state, blockNumbers, solPerBlock) {
    const newDeployments = { ...state.userDeployments };

    blockNumbers.forEach(blockNum => {
        newDeployments[blockNum] = (newDeployments[blockNum] || 0) + solPerBlock;
    });

    const totalDeployed = Object.values(newDeployments).reduce((sum, val) => sum + val, 0);

    return {
        ...state,
        userDeployments: newDeployments,
        totalDeployedSOL: totalDeployed,
        isActive: true,
    };
}

/**
 * Claim rewards
 *
 * @param {Object} state - Current mining state
 * @param {boolean} claimMINER - Whether to claim MINER (with 10% fee)
 * @param {boolean} claimSOL - Whether to claim SOL
 * @returns {Object} { newState, claimedMINER, claimedSOL, fee }
 */
export function claimRewards(state, claimMINER = true, claimSOL = true) {
    let claimedMINER = 0;
    let claimedSOL = 0;
    let fee = 0;
    let newRefinedMINER = state.refinedMINER;

    if (claimMINER && state.unrefinedMINER > 0) {
        // Apply 10% claiming fee
        fee = state.unrefinedMINER * (MINING_CONFIG.CLAIMING_FEE_PERCENT / 100);
        claimedMINER = state.unrefinedMINER - fee;

        // Fee goes to refined MINER pool
        newRefinedMINER += fee;
    }

    if (claimSOL && state.pendingSOLRewards > 0) {
        claimedSOL = state.pendingSOLRewards;
    }

    return {
        newState: {
            ...state,
            unrefinedMINER: claimMINER ? 0 : state.unrefinedMINER,
            pendingSOLRewards: claimSOL ? 0 : state.pendingSOLRewards,
            refinedMINER: newRefinedMINER,
        },
        claimedMINER,
        claimedSOL,
        fee,
    };
}

/**
 * Update timer (called every second)
 *
 * @param {Object} state - Current mining state
 * @returns {Object} { newState, roundEnded }
 */
export function updateTimer(state) {
    const newTimeLeft = state.timeLeft - 1;

    if (newTimeLeft <= 0) {
        // Round ended
        return {
            newState: {
                ...state,
                timeLeft: 0,
            },
            roundEnded: true,
        };
    }

    return {
        newState: {
            ...state,
            timeLeft: newTimeLeft,
        },
        roundEnded: false,
    };
}
